import React, { Component } from "react";
import moment from "moment";
import Bridge from "../../utils/Bridge";
import { logEvent } from "../../utils/analytics";
import { Spin, message } from "antd";
import "./PrintReport.css";

export default class PrintReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      respiratoryData: [],
      mptData: [],
      alsfrsrData: [],
      inclinicAlsfrsrData: [],
      speechData: [],
      eals_id: this.props.eals_id,
    };
  }

  componentDidMount() {
    this.fetchAllData();
  }

  fetchAllData = async () => {
    this.setState({ loading: true });
    try {
      // Fetch all data in parallel
      await Promise.all([
        this.fetchRespiratoryData(),
        this.fetchMPTData(),
        this.fetchALSFRSRData(),
        this.fetchInClinicALSFRSRData(),
        this.fetchSpeechData(),
      ]);
      this.setState({ loading: false });
    } catch (error) {
      console.error("Error in fetchAllData:", error);
      const lang = this.props.formData?.page?.printReport || {};
      message.error(lang.failedToLoadReportData || "Failed to load report data");
      this.setState({ loading: false, speechData: [] });
    }
  };

  fetchRespiratoryData = () => {
    let params = "?eals_id=" + this.state.eals_id;
    return Bridge.personal_data_viewer.le_study
      .get_le_study_in_clinic_session_list(params)
      .then((response) => {
        if (response.status == "1") {
          this.setState({ respiratoryData: response.data });
        }
      })
      .catch((error) => {
        console.error("Error fetching respiratory data:", error);
      });
  };

  fetchMPTData = () => {
    let params = "?eals_id=" + this.state.eals_id;
    return Bridge.personal_data_viewer.le_study
      .get_le_study_in_clinic_session_list(params)
      .then((response) => {
        if (response.status == "1") {
          this.setState({ mptData: response.data });
        }
      })
      .catch((error) => {
        console.error("Error fetching MPT data:", error);
      });
  };

  fetchALSFRSRData = () => {
    let params = "?eals_id=" + this.state.eals_id;
    return Bridge.personal_data_viewer.le_study
      .get_le_study_eals_frs_score(params)
      .then((response) => {
        if (response.status == "1") {
          // Sort by date descending
          const sortedData = response.data.sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
            const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
            return dateB - dateA; // Descending order (newest first)
          });
          this.setState({ alsfrsrData: sortedData });
        }
      })
      .catch((error) => {
        console.error("Error fetching ALSFRS-R data:", error);
      });
  };

  fetchInClinicALSFRSRData = () => {
    let params = "?eals_id=" + this.state.eals_id;
    return Bridge.personal_data_viewer.le_study
      .get_le_study_inclinic_alsfrs_score(params)
      .then((response) => {
        if (response.status == "1") {
          // Sort by session_date (in-clinic) descending
          const sortedData = response.data.sort((a, b) => {
            const dateA = (a.session_date || a.created_at) ? new Date(a.session_date || a.created_at) : new Date(0);
            const dateB = (b.session_date || b.created_at) ? new Date(b.session_date || b.created_at) : new Date(0);
            return dateB - dateA; // Descending order (newest first)
          });
          this.setState({ inclinicAlsfrsrData: sortedData });
        }
      })
      .catch((error) => {
        console.error("Error fetching In-Clinic ALSFRS-R data:", error);
      });
  };

  fetchSpeechData = () => {
    let params = "?eals_id=" + this.state.eals_id;
    return Bridge.personal_data_viewer.le_study
      .get_le_study_session_list(params)
      .then((response) => {
        if (response.status == "1" && response.data && response.data.length > 0) {
          // Newest first so speechData[0] is the latest session
          const sortedData = [...response.data].sort((a, b) => {
            const dateA = a.date_created ? new Date(a.date_created) : new Date(0);
            const dateB = b.date_created ? new Date(b.date_created) : new Date(0);
            return dateB - dateA;
          });
          this.setState({ speechData: sortedData });
        } else {
          this.setState({ speechData: [] });
        }
      })
      .catch((error) => {
        console.error("Error fetching speech data:", error);
        this.setState({ speechData: [] });
      });
  };

  handlePrint = () => {
    logEvent("report_printed");
    // Add a style tag to prevent URLs from showing in print
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        @page {
          margin-bottom: 0.5cm !important;
        }
        a[href]:after {
          content: none !important;
        }
        a {
          text-decoration: none !important;
          color: inherit !important;
        }
        /* Hide any content in page margins */
        body::after {
          content: none !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // Print
    window.print();
    
    // Remove the style after printing
    setTimeout(() => {
      document.head.removeChild(style);
    }, 1000);
  };

  render() {
    const { loading, respiratoryData, mptData, alsfrsrData, inclinicAlsfrsrData, speechData } =
      this.state;
    const { formData, onClose } = this.props;
    const lang = formData?.page?.printReport || {};
    const hasAnyData =
      respiratoryData.length > 0 ||
      mptData.length > 0 ||
      alsfrsrData.length > 0 ||
      inclinicAlsfrsrData.length > 0 ||
      speechData.length > 0;

    // Debug log
    console.log(
      "PrintReport render - speechData:",
      speechData,
      "length:",
      speechData.length,
      
    );

    return (
      <div className="print-report-container">
        <Spin spinning={loading}>
          <div className="print-header no-print">
            <h2>
              {lang.title || "My Health Report"}
            </h2>
            <div className="print-actions">
              <button
                onClick={this.handlePrint}
                className="print-button"
                disabled={!hasAnyData}
              >
                {lang.printSaveAsPdf || "Print / Save as PDF"}
              </button>
              <button onClick={onClose} className="close-button">
                {lang.close || "Close"}
              </button>
            </div>
          </div>

          {!hasAnyData && !loading && (
            <div
              className="no-data-message"
              style={{ padding: "40px", textAlign: "center" }}
            >
              <h3>{lang.noDataAvailable || "No Data Available"}</h3>
              <p>
                {lang.noDataMessage || "You don't have any session data yet. Complete your first session to see your health report."}
              </p>
            </div>
          )}

          {hasAnyData && (
            <div className="print-content">
              {/* Header for printed page */}
              <div className="print-only print-page-header">
                <h1>{lang.personalHealthReport || "Personal Health Report"}</h1>
                <p>{lang.generatedOn || "Generated on:"} {moment().format("MMMM DD, YYYY")}</p>
                <hr />
              </div>

              {/* Session Overview - Digital vs In Clinic comparison (matches report design) */}
              <section className="report-section">
                <h2 className="section-title">{lang.sessionOverview || "Session Overview"}</h2>
                <div className="section-content session-overview-table-wrapper">
                  <table className="session-overview-table">
                    <thead>
                      <tr>
                        <th colSpan="2">{lang.digital || "Digital"}</th>
                        <th colSpan="2">{lang.inClinic || "In Clinic"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="session-overview-label">
                          {lang.totalDigitalSpeechSessions || "Total Digital Speech Sessions:"}
                        </td>
                        <td>{speechData.length || 0}</td>
                        <td className="session-overview-label">
                          {lang.totalInClinicSpeechSessions || "Total In Clinic Speech Sessions:"}
                        </td>
                        <td>{respiratoryData.length || 0}</td>
                      </tr>
                      <tr>
                        <td className="session-overview-label">
                          {lang.latestDigitalSpeechSession || "Latest Digital Speech Session:"}
                        </td>
                        <td>
                          {speechData.length > 0
                            ? moment(speechData[0]?.date_created).format("MM/DD/YYYY")
                            : "---"}
                        </td>
                        <td className="session-overview-label">
                          {lang.latestInClinicSpeechSession || "Latest In Clinic Speech Session:"}
                        </td>
                        <td>
                          {respiratoryData.length > 0
                            ? moment(respiratoryData[0]?.entry_date).format("MM/DD/YYYY")
                            : "---"}
                        </td>
                      </tr>
                      <tr>
                        <td className="session-overview-label">
                          {lang.digitalALSFRSRScores || "Digital ALSFRS-R Scores:"}
                        </td>
                        <td>
                          {alsfrsrData.length > 0 && alsfrsrData[0]?.ALS_total != null
                            ? alsfrsrData[0].ALS_total
                            : "---"}
                        </td>
                        <td className="session-overview-label">
                          {lang.inClinicALSFRSRScores || "In Clinic ALSFRS-R Scores:"}
                        </td>
                        <td>
                          {inclinicAlsfrsrData.length > 0 && inclinicAlsfrsrData[0]?.ALS_total != null
                            ? inclinicAlsfrsrData[0].ALS_total
                            : "---"}
                        </td>
                      </tr>
                      <tr>
                        <td className="session-overview-label">
                          {lang.latestDigitalALSFRSRDate || "Latest Digital ALSFRS-R Date:"}
                        </td>
                        <td>
                          {alsfrsrData.length > 0 && alsfrsrData[0]?.created_at
                            ? moment(alsfrsrData[0].created_at).format("MM/DD/YYYY")
                            : "---"}
                        </td>
                        <td className="session-overview-label">
                          {lang.latestInClinicALSFRSRDate || "Latest In Clinic ALSFRS-R Date:"}
                        </td>
                        <td>
                          {inclinicAlsfrsrData.length > 0 && inclinicAlsfrsrData[0]?.session_date
                            ? moment(inclinicAlsfrsrData[0].session_date).format("MM/DD/YYYY")
                            : "---"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Digital ALSFRS-R Scores */}
              {alsfrsrData.length > 0 && (
                <section className="report-section">
                  <h2 className="section-title">{lang.digitalALSFRSRScores || "Digital ALSFRS-R Scores"}</h2>
                  <div className="section-content">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>{lang.date || "Date"}</th>
                          <th>{lang.speech || "Speech"}</th>
                          <th>{lang.saliv || "Saliv."}</th>
                          <th>{lang.swall || "Swall."}</th>
                          <th>{lang.hand || "Hand."}</th>
                          <th>{lang.cutFood || "Cut Food"}</th>
                          <th>{lang.dress || "Dress."}</th>
                          <th>{lang.turn || "Turn"}</th>
                          <th>{lang.walk || "Walk"}</th>
                          <th>{lang.stairs || "Stairs"}</th>
                          <th>{lang.dysp || "Dysp."}</th>
                          <th>{lang.ortho || "Ortho."}</th>
                          <th>{lang.respInsuf || "Resp. Insuf."}</th>
                          <th>{lang.total || "Total"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alsfrsrData.map((item, index) => (
                          <tr key={index}>
                            <td>
                              {moment(item.created_at).format("MM/DD/YYYY")}
                            </td>
                            <td>{item.speech !== null && item.speech !== undefined && item.speech !== "" ? item.speech : "---"}</td>
                            <td>{item.salvation !== null && item.salvation !== undefined && item.salvation !== "" ? item.salvation : "---"}</td>
                            <td>{item.swallowing !== null && item.swallowing !== undefined && item.swallowing !== "" ? item.swallowing : "---"}</td>
                            <td>{item.handwriting !== null && item.handwriting !== undefined && item.handwriting !== "" ? item.handwriting : "---"}</td>
                            <td>
                              {(item.cutting_food_a !== null && item.cutting_food_a !== undefined && item.cutting_food_a !== "") ||
                              (item.cutting_food_b !== null && item.cutting_food_b !== undefined && item.cutting_food_b !== "")
                                ? (item.cutting_food_a || item.cutting_food_b)
                                : "---"}
                            </td>
                            <td>{item.dressing_and_hygiene !== null && item.dressing_and_hygiene !== undefined && item.dressing_and_hygiene !== "" ? item.dressing_and_hygiene : "---"}</td>
                            <td>{item.turning_in_bed !== null && item.turning_in_bed !== undefined && item.turning_in_bed !== "" ? item.turning_in_bed : "---"}</td>
                            <td>{item.walking !== null && item.walking !== undefined && item.walking !== "" ? item.walking : "---"}</td>
                            <td>{item.climbing_stairs !== null && item.climbing_stairs !== undefined && item.climbing_stairs !== "" ? item.climbing_stairs : "---"}</td>
                            <td>{item.dyspnea !== null && item.dyspnea !== undefined && item.dyspnea !== "" ? item.dyspnea : "---"}</td>
                            <td>{item.orthopnea !== null && item.orthopnea !== undefined && item.orthopnea !== "" ? item.orthopnea : "---"}</td>
                            <td>{item.respiratory_insufficiency !== null && item.respiratory_insufficiency !== undefined && item.respiratory_insufficiency !== "" ? item.respiratory_insufficiency : "---"}</td>
                            <td>
                              <strong>{item.ALS_total !== null && item.ALS_total !== undefined && item.ALS_total !== "" ? item.ALS_total : "---"}</strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* In-Clinic ALSFRS-R Scores */}
              {inclinicAlsfrsrData.length > 0 && (
                <section className="report-section">
                  <h2 className="section-title">{lang.inclinicALSFRSRScores || "In-Clinic ALSFRS-R Scores"}</h2>
                  <div className="section-content">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>{lang.date || "Date"}</th>
                          <th>{lang.speech || "Speech"}</th>
                          <th>{lang.saliv || "Saliv."}</th>
                          <th>{lang.swall || "Swall."}</th>
                          <th>{lang.hand || "Hand."}</th>
                          <th>{lang.cutFood || "Cut Food"}</th>
                          <th>{lang.dress || "Dress."}</th>
                          <th>{lang.turn || "Turn"}</th>
                          <th>{lang.walk || "Walk"}</th>
                          <th>{lang.stairs || "Stairs"}</th>
                          <th>{lang.dysp || "Dysp."}</th>
                          <th>{lang.ortho || "Ortho."}</th>
                          <th>{lang.respInsuf || "Resp. Insuf."}</th>
                          <th>{lang.total || "Total"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inclinicAlsfrsrData.map((item, index) => (
                          <tr key={index}>
                            <td>
                              {item.session_date
                                ? moment(item.session_date).format("MM/DD/YYYY")
                                : "---"}
                            </td>
                            <td>{item.speech !== null && item.speech !== undefined && item.speech !== "" ? item.speech : 0}</td>
                            <td>{item.salvation !== null && item.salvation !== undefined && item.salvation !== "" ? item.salvation : 0}</td>
                            <td>{item.swallowing !== null && item.swallowing !== undefined && item.swallowing !== "" ? item.swallowing : 0}</td>
                            <td>{item.handwriting !== null && item.handwriting !== undefined && item.handwriting !== "" ? item.handwriting : 0}</td>
                            <td>
                              {(item.cutting_food_a !== null && item.cutting_food_a !== undefined && item.cutting_food_a !== "") ||
                              (item.cutting_food_b !== null && item.cutting_food_b !== undefined && item.cutting_food_b !== "")
                                ? (item.cutting_food_a || item.cutting_food_b)
                                : 0}
                            </td>
                            <td>{item.dressing_and_hygiene !== null && item.dressing_and_hygiene !== undefined && item.dressing_and_hygiene !== "" ? item.dressing_and_hygiene : 0}</td>
                            <td>{item.turning_in_bed !== null && item.turning_in_bed !== undefined && item.turning_in_bed !== "" ? item.turning_in_bed : 0}</td>
                            <td>{item.walking !== null && item.walking !== undefined && item.walking !== "" ? item.walking : 0}</td>
                            <td>{item.climbing_stairs !== null && item.climbing_stairs !== undefined && item.climbing_stairs !== "" ? item.climbing_stairs : 0}</td>
                            <td>{item.dyspnea !== null && item.dyspnea !== undefined && item.dyspnea !== "" ? item.dyspnea : 0}</td>
                            <td>{item.orthopnea !== null && item.orthopnea !== undefined && item.orthopnea !== "" ? item.orthopnea : 0}</td>
                            <td>{item.respiratory_insufficiency !== null && item.respiratory_insufficiency !== undefined && item.respiratory_insufficiency !== "" ? item.respiratory_insufficiency : 0}</td>
                            <td>
                              <strong>{item.ALS_total !== null && item.ALS_total !== undefined && item.ALS_total !== "" ? item.ALS_total : 0}</strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* FVC Data */}
              {respiratoryData.length > 0 && (
                <section className="report-section fvc-table-section">
                  <h2 className="section-title">
                    {(lang.inClinic || "In Clinic") +
                      " - " +
                      (lang.forcedVitalCapacity || "Forced Vital Capacity (FVC)")}
                  </h2>
                  <div className="section-content">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th rowSpan="2">{lang.date || "Date"}</th>
                          <th colSpan="3">{lang.fvc1 || "FVC 1"}</th>
                          <th colSpan="3">{lang.fvc2 || "FVC 2"}</th>
                          <th colSpan="3">{lang.fvc3 || "FVC 3"}</th>
                          <th colSpan="3">{lang.greatest || "Greatest"}</th>
                        </tr>
                        <tr>
                          <th>{lang.percentP || "%P"}</th>
                          <th>{lang.ml || "mL"}</th>
                          <th>{lang.l || "L"}</th>
                          <th>{lang.percentP || "%P"}</th>
                          <th>{lang.ml || "mL"}</th>
                          <th>{lang.l || "L"}</th>
                          <th>{lang.percentP || "%P"}</th>
                          <th>{lang.ml || "mL"}</th>
                          <th>{lang.l || "L"}</th>
                          <th>{lang.percentP || "%P"}</th>
                          <th>{lang.ml || "mL"}</th>
                          <th>{lang.l || "L"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {respiratoryData
                          .sort(
                            (a, b) =>
                              new Date(b.entry_date) - new Date(a.entry_date)
                          )
                          .map((item, index) => (
                            <tr key={index}>
                              <td>
                                {moment(item.entry_date).format("MM/DD/YYYY")}
                              </td>
                              <td>{item.fvc1 || "---"}</td>
                              <td>{item.fvc1Raw || "---"}</td>
                              <td>{item.fvc1RawL || "---"}</td>
                              <td>{item.fvc2 || "---"}</td>
                              <td>{item.fvc2Raw || "---"}</td>
                              <td>{item.fvc2RawL || "---"}</td>
                              <td>{item.fvc3 || "---"}</td>
                              <td>{item.fvc3Raw || "---"}</td>
                              <td>{item.fvc3RawL || "---"}</td>
                              <td>
                                <strong>{item.greatestFVC || "---"}</strong>
                              </td>
                              <td>
                                <strong>{item.greatestFVCRaw || "---"}</strong>
                              </td>
                              <td>
                                <strong>{item.greatestFVCRawL || "---"}</strong>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* PCF Data */}
              {respiratoryData.length > 0 && (
                <section className="report-section">
                  <h2 className="section-title">
                    {(lang.inClinic || "In Clinic") +
                      " - " +
                      (lang.peakCoughFlow || "Peak Cough Flow (PCF)")}
                  </h2>
                  <div className="section-content">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>{lang.sessionDate || "Session Date"}</th>
                          <th>{lang.pcf1 || "PCF 1 (L/min)"}</th>
                          <th>{lang.pcf2 || "PCF 2 (L/min)"}</th>
                          <th>{lang.pcf3 || "PCF 3 (L/min)"}</th>
                          <th>{lang.greatestPCF || "Greatest PCF (L/min)"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {respiratoryData
                          .sort(
                            (a, b) =>
                              new Date(b.entry_date) - new Date(a.entry_date)
                          )
                          .map((item, index) => (
                            <tr key={index}>
                              <td>
                                {moment(item.entry_date).format("MM/DD/YYYY")}
                              </td>
                              <td>{item.pcf1 || "---"}</td>
                              <td>{item.pcf2 || "---"}</td>
                              <td>{item.pcf3 || "---"}</td>
                              <td>
                                <strong>{item.greatestPCF || "---"}</strong>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* MPT Data */}
              {mptData.length > 0 && (
                <section className="report-section">
                  <h2 className="section-title">
                    {(lang.inClinic || "In Clinic") +
                      " - " +
                      (lang.maximumPhonationTime ||
                        "Maximum Phonation Time (MPT)")}
                  </h2>
                  <div className="section-content">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>{lang.sessionDate || "Session Date"}</th>
                          <th>{lang.mpt1 || "MPT 1 (seconds)"}</th>
                          <th>{lang.mpt2 || "MPT 2 (seconds)"}</th>
                          <th>{lang.mpt3 || "MPT 3 (seconds)"}</th>
                          <th>{lang.greatestMPT || "Greatest MPT (seconds)"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mptData
                          .sort(
                            (a, b) =>
                              new Date(b.entry_date) - new Date(a.entry_date)
                          )
                          .map((item, index) => {
                            // Calculate greatest MPT from mpt1, mpt2, mpt3
                            const mptValues = [
                              item.mpt1 !== null && item.mpt1 !== undefined
                                ? item.mpt1
                                : null,
                              item.mpt2 !== null && item.mpt2 !== undefined
                                ? item.mpt2
                                : null,
                              item.mpt3 !== null && item.mpt3 !== undefined
                                ? item.mpt3
                                : null,
                            ].filter((val) => val !== null);
                            const greatestMPT =
                              mptValues.length > 0
                                ? Math.max(...mptValues)
                                : null;

                            return (
                              <tr key={index}>
                                <td>
                                  {moment(item.entry_date).format("MM/DD/YYYY")}
                                </td>
                                <td>
                                  {item.mpt1 !== null && item.mpt1 !== undefined
                                    ? item.mpt1
                                    : "---"}
                                </td>
                                <td>
                                  {item.mpt2 !== null && item.mpt2 !== undefined
                                    ? item.mpt2
                                    : "---"}
                                </td>
                                <td>
                                  {item.mpt3 !== null && item.mpt3 !== undefined
                                    ? item.mpt3
                                    : "---"}
                                </td>
                                <td>
                                  <strong>
                                    {greatestMPT !== null ? greatestMPT : "---"}
                                  </strong>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
              

              {/* Speech Sessions Data */}
              {speechData && speechData.length > 0 && (
                <section className="report-section">
                  <h2 className="section-title">
                    {(lang.digital || "Digital") +
                      " - " +
                      (lang.speechSessions || "Speech Sessions")}
                  </h2>
                  <div className="section-content">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>{lang.sessionDate || "Session Date"}</th>
                          <th>{lang.englishSpeakingRateWPM || "English Speaking Rate (WPM)"}</th>
                          <th>{lang.spanishSpeakingRateWPM || "Spanish Speaking Rate (WPM)"}</th>
                          <th>{lang.sessionDuration || "Session Duration"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {speechData
                          .filter((item) => item && item.date_created)
                          .sort((a, b) => {
                            try {
                              const dateA = a.date_created
                                ? moment(a.date_created)
                                : moment(0);
                              const dateB = b.date_created
                                ? moment(b.date_created)
                                : moment(0);
                              return dateB - dateA;
                            } catch (e) {
                              return 0;
                            }
                          })
                          .map((item, index) => {
                            let sessionDate = "---";
                            try {
                              if (item.date_created) {
                                sessionDate = moment(item.date_created).format(
                                  "MM/DD/YYYY"
                                );
                              }
                            } catch (e) {
                              sessionDate = "---";
                            }
                            
                            // Calculate English WPM (speaking_rate * 60)
                            const englishWPM = 
                              item.speaking_rate !== null &&
                              item.speaking_rate !== undefined &&
                              item.speaking_rate !== ""
                                ? parseFloat((parseFloat(item.speaking_rate) * 60).toFixed(2))
                                : null;
                            
                            // Calculate Spanish WPM (spanish_speaking_rate * 60)
                            const spanishWPM = 
                              item.spanish_speaking_rate !== null &&
                              item.spanish_speaking_rate !== undefined &&
                              item.spanish_speaking_rate !== ""
                                ? parseFloat((parseFloat(item.spanish_speaking_rate) * 60).toFixed(2))
                                : null;
                            
                            return (
                              <tr key={`speech-${item.session_id || index}`}>
                                <td>{sessionDate}</td>
                                <td>
                                  {englishWPM !== null && !isNaN(englishWPM) ? englishWPM : "---"}
                                </td>
                                <td>
                                  {spanishWPM !== null && !isNaN(spanishWPM) ? spanishWPM : "---"}
                                </td>
                                <td>
                                  {item.total_duration || "---"}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* Footer for printed page */}
              <div className="print-only print-page-footer">
                <hr />
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  {lang.footerText || "Generated from EverythingALS Patient Portal -"}{" "}
                  {moment().format("MMMM DD, YYYY")}
                </p>
              </div>
            </div>
          )}
        </Spin>
      </div>
    );
  }
}
